import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MergeIcon from "@mui/icons-material/Merge";
import api from "../../axiosInstance";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import ComboDiscountModal, { COMBO_ITEMS } from "./ComboDiscountModal";
import ComboDiscountMappingModal from "./ComboDiscountMappingModal";

const headerCell = {
  padding: "10px 5px",
  backgroundColor: "#17479e",
  color: "white",
  fontWeight: 600,
  textAlign: "center",
};

const formatPercent = (value) => (value == null ? "—" : `${Number(value)}%`);

// How many course + regulation + semester combinations the discount is mapped to.
const formatMapping = (count) =>
  !count ? "Not mapped" : `${count} combination${count === 1 ? "" : "s"}`;

function ComboDiscount() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [comboEdit, setComboEdit] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  // The discount whose academic mapping is open (Banner Course Mapping pattern).
  const [comboMap, setComboMap] = useState(null);

  // `quiet`: refresh in place (no spinner), e.g. after a mapping save.
  const loadRows = (quiet = false) => {
    if (!quiet) setIsLoading(true);
    setError(null);
    return api
      .get("/api/combo-discount-prices")
      .then((res) => setRows(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.errorMessage ||
            "Failed to load combo discounts. Please try again.",
        );
      })
      .finally(() => {
        if (!quiet) setIsLoading(false);
      });
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saving a mapping can move combinations away from other discounts (latest
  // mapping wins), so every row's "Applies to" count is reloaded.
  const handleMappingSuccess = (saved) => {
    handleFormSuccess(saved);
    loadRows(true);
  };

  const handleFormSuccess = (saved) => {
    if (!saved?.id) return;
    setRows((prev) =>
      prev.some((r) => r.id === saved.id)
        ? prev.map((r) => (r.id === saved.id ? saved : r))
        : [...prev, saved],
    );
  };

  const openAdd = () => {
    setComboEdit(null);
    setOpenForm(true);
  };

  const openEdit = (row) => {
    setComboEdit(row);
    setOpenForm(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/combo-discount-prices/${deleteId}`);
      setRows((prev) => prev.filter((r) => r.id !== deleteId));
      toast.success("Combo discount deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to delete the combo discount. Please try again.",
      );
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <ToastContainer />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, mt: 1 }}>
        <Link underline="hover" color="inherit" to="/booklist">
          Books List
        </Link>
        <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
          Combo Discount
        </Typography>
      </Breadcrumbs>

      <Card sx={{ boxShadow: 1, mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Discount percentage applied when the cart has 1 to 6 books.{" "}
              {rows.length} configuration{rows.length === 1 ? "" : "s"}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openAdd}
              {...BUTTON_STYLES.primary}
              sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
            >
              Add New Combo Discount
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small" aria-label="combo discounts table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCell}>ID</TableCell>
                    {COMBO_ITEMS.map((i) => (
                      <TableCell key={i.key} sx={headerCell}>
                        {i.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...headerCell, minWidth: 120 }}>Applies to</TableCell>
                    <TableCell sx={{ ...headerCell, minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row) => (
                      <TableRow
                        hover
                        key={row.id}
                        sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0, 0, 0, 0.02)" } }}
                      >
                        <TableCell align="center">{row.id}</TableCell>
                        {COMBO_ITEMS.map((i) => (
                          <TableCell key={i.key} align="center">
                            {formatPercent(row[i.key])}
                          </TableCell>
                        ))}
                        <TableCell align="center">{formatMapping(row.mappedCombinations)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Edit Combo Discount" arrow placement="top">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => openEdit(row)}
                                sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Combo Discount" arrow placement="top">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => setDeleteId(row.id)}
                                sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={row.mappedCombinations ? "Edit Academic Mapping" : "Add Academic Mapping"}
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                aria-label={`Academic mapping for combo discount ${row.id}`}
                                onClick={() => setComboMap(row)}
                                sx={{ color: "#0b6a3e", "&:hover": { backgroundColor: "#0b6a3e", color: "white" } }}
                              >
                                <MergeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={COMBO_ITEMS.length + 3} sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                          No combo discounts available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Fade>
      )}

      <ComboDiscountMappingModal
        open={Boolean(comboMap)}
        onClose={() => setComboMap(null)}
        comboMap={comboMap}
        onSuccess={handleMappingSuccess}
      />
      <ComboDiscountModal
        open={openForm}
        comboEdit={comboEdit}
        onClose={() => setOpenForm(false)}
        onSuccess={handleFormSuccess}
      />

      <DeleteConfirmationDialog
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this combo discount? This action cannot be undone."
        additionalWarning="A combo discount that is assigned to a course cannot be deleted."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default ComboDiscount;
